export const SUITS = ['C', 'D', 'H', 'S'] as const;
export type Suit = (typeof SUITS)[number];
export const RANKS = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'] as const;
export type Rank = (typeof RANKS)[number];
export type Joker = 'BJ' | 'LJ';
/** "AS" (ace of spades), "TD" (ten of diamonds), "BJ"/"LJ" (the high and low jokers). */
export type Card = `${Rank}${Suit}` | Joker;

export const SUIT_NAMES: Record<Suit, string> = { C: 'clubs', D: 'diamonds', H: 'hearts', S: 'spades' };

export const DECK: readonly Card[] = [
  ...SUITS.flatMap((s) => RANKS.map((r): Card => `${r}${s}`)),
  'BJ',
  'LJ',
];

export function isCard(x: unknown): x is Card {
  return typeof x === 'string' && (DECK as readonly string[]).includes(x);
}

export function isSuit(x: unknown): x is Suit {
  return typeof x === 'string' && (SUITS as readonly string[]).includes(x);
}

/** The other suit of the same color: clubs ↔ spades, diamonds ↔ hearts. */
export function offSuit(s: Suit): Suit {
  return ({ C: 'S', S: 'C', D: 'H', H: 'D' } as const)[s];
}

/**
 * The 17 playable cards for a named suit, strongest first. Rank and
 * playability both come from this one list, so the off-cards and jokers are
 * data rather than special cases.
 */
export function trumpOrder(t: Suit): Card[] {
  const o = offSuit(t);
  return [
    `A${t}`, `K${t}`, `Q${t}`, `J${t}`, `J${o}`, 'BJ', 'LJ',
    `T${t}`, `9${t}`, `8${t}`, `7${t}`, `6${t}`, `5${t}`, `4${t}`, `3${t}`, `3${o}`, `2${t}`,
  ];
}

const ORDER_CACHE = new Map<Suit, Map<Card, number>>(
  SUITS.map((t) => {
    const order = trumpOrder(t);
    return [t, new Map(order.map((c, i) => [c, order.length - i]))];
  }),
);

/** Higher beats lower; 0 means not playable under this suit. */
export function strength(card: Card, trump: Suit): number {
  return ORDER_CACHE.get(trump)!.get(card) ?? 0;
}

export function isPlayable(card: Card, trump: Suit): boolean {
  return strength(card, trump) > 0;
}

/** Points a card is worth under a named suit (13 in total per suit). */
export function pointsOf(card: Card, trump: Suit): number {
  if (!isPlayable(card, trump)) return 0;
  if (card === 'BJ' || card === 'LJ') return 1;
  switch (card[0]) {
    case 'A':
    case 'J':
    case 'T':
    case '2':
      return 1;
    case '3':
      return 3;
    default:
      return 0;
  }
}

/** The 2 is scored by whoever plays it; it can't be captured. */
export function isTwo(card: Card, trump: Suit): boolean {
  return card === `2${trump}`;
}

export function cardName(card: Card): string {
  if (card === 'BJ') return 'High Joker';
  if (card === 'LJ') return 'Low Joker';
  const rank = ({ A: 'Ace', K: 'King', Q: 'Queen', J: 'Jack', T: '10' } as Record<string, string>)[card[0]!] ?? card[0]!;
  return `${rank} of ${SUIT_NAMES[card[1] as Suit]}`;
}
